import { z } from 'zod';
import { MAX_AMOUNT, MAX_NOTE_LENGTH } from '../parser/parse-entry';

export const ENTRY_TYPES = ['INCOME', 'EXPENSE'] as const;

export const idSchema = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[\w-]+$/);

const noteSchema = z
  .string()
  .trim()
  .max(MAX_NOTE_LENGTH, `Catatan maksimal ${MAX_NOTE_LENGTH} karakter`)
  .transform((v) => (v.length > 0 ? v : null));

export const amountSchema = z
  .number({ error: 'Nominal wajib diisi' })
  .int('Nominal harus bilangan bulat')
  .positive('Nominal harus lebih dari 0')
  .max(MAX_AMOUNT, 'Nominal terlalu besar');

export const transactionCreateSchema = z.object({
  categoryId: idSchema,
  amount: amountSchema,
  note: noteSchema.nullish(),
  occurredAt: z.coerce.date().optional(),
});
export type TransactionCreateInput = z.input<typeof transactionCreateSchema>;

export const transactionUpdateSchema = transactionCreateSchema.partial();
export type TransactionUpdateInput = z.input<typeof transactionUpdateSchema>;

export const transactionFilterSchema = z.object({
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  categoryId: idSchema.optional(),
  type: z.enum(ENTRY_TYPES).optional(),
  search: z.string().trim().max(100).optional(),
  createdById: idSchema.optional(),
  cursor: idSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});
export type TransactionFilterInput = z.input<typeof transactionFilterSchema>;
export type TransactionFilter = z.output<typeof transactionFilterSchema>;

const aliasSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[\p{L}\p{N}_-]{1,30}$/u, 'Kata kunci satu kata, huruf/angka saja, maks 30 karakter');

export const CATEGORY_ICONS = [
  'utensils',
  'coffee',
  'car',
  'fuel',
  'shopping-bag',
  'shopping-cart',
  'receipt',
  'zap',
  'wifi',
  'home',
  'popcorn',
  'gamepad',
  'heart-pulse',
  'graduation-cap',
  'gift',
  'plane',
  'shirt',
  'baby',
  'paw',
  'circle-dashed',
  'wallet',
  'coins',
  'briefcase',
  'trending-up',
] as const;
export type CategoryIcon = (typeof CATEGORY_ICONS)[number];

export const categoryCreateSchema = z.object({
  name: z.string().trim().min(1, 'Nama wajib diisi').max(40),
  type: z.enum(ENTRY_TYPES),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Warna harus format #RRGGBB'),
  icon: z.enum(CATEGORY_ICONS),
  aliases: z
    .array(aliasSchema)
    .max(20)
    .default([])
    .transform((list) => [...new Set(list)]),
});
export type CategoryCreateInput = z.input<typeof categoryCreateSchema>;

export const categoryUpdateSchema = categoryCreateSchema.omit({ type: true }).partial();
export type CategoryUpdateInput = z.input<typeof categoryUpdateSchema>;

export const categoryDeleteSchema = z.object({ reassignTo: idSchema.optional() });

export const ledgerNameSchema = z.object({ name: z.string().trim().min(1, 'Nama wajib diisi').max(60) });
export type LedgerNameInput = z.input<typeof ledgerNameSchema>;
